import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  X, 
  ArrowRight, 
  ArrowLeft, 
  Package, 
  Ruler,
  Sparkles,
  Rocket,
  Compass,
} from "lucide-react";

interface OnboardingWalkthroughProps {
  userProfile: any;
  onComplete: () => void;
  forceShow?: boolean; // Allow external trigger to show tour
}

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  route?: string; // Optional route to navigate to
  accentColor: string;
  tip?: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to ClubIQ",
    description: "Your bag is set up from the quiz. Let's take a quick tour so you know where everything lives — your clubs, distances, and the optimizer that powers your recommendations.",
    icon: Compass,
    route: "/my-golf-bag",
    accentColor: "from-indigo-500 to-purple-600",
    tip: "This tour takes about 30 seconds. You can skip anytime.",
  },
  {
    id: "mybag",
    title: "Step 2: Build Your Bag",
    description: "Add every club in your bag — driver, woods, hybrids, irons, wedges, and putter. Click any club to edit brand, model, shaft, and grip details. You can also snap a photo to auto-identify your clubs.",
    icon: Package,
    route: "/my-golf-bag",
    accentColor: "from-green-500 to-emerald-600",
    tip: "This is the foundation. Everything starts with your bag.",
  },
  {
    id: "mydistances",
    title: "Step 3: Confirm Your Distances",
    description: "Switch to the Distances tab and review your carry distances. Tap any distance to edit it. Accurate numbers power every recommendation.",
    icon: Ruler,
    route: "/my-golf-bag?tab=distances",
    accentColor: "from-blue-500 to-indigo-600",
    tip: "Be honest with your distances. Better data means better recommendations.",
  },
  {
    id: "optimizer",
    title: "Step 4: Run the Optimizer",
    description: "Once your bag and distances are set, head to the Club Optimizer. It will analyze your bag for gaps, recommend upgrades, advise on shots, and help you find the right ball — all in one place.",
    icon: Sparkles,
    route: "/club-optimizer",
    accentColor: "from-purple-500 to-violet-600",
    tip: "The optimizer unlocks after your bag is complete.",
  },
];

const OnboardingWalkthrough: React.FC<OnboardingWalkthroughProps> = ({ 
  userProfile, 
  onComplete,
  forceShow = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle external force show trigger
  useEffect(() => {
    if (forceShow) {
      setCurrentStep(0); // Reset to first step
      setIsVisible(true);
    }
  }, [forceShow]);

  useEffect(() => {
    // Only auto-show after quiz AND after clubs have been added to the bag
    if (!forceShow && (userProfile?.has_completed_onboarding || userProfile?.hasCompletedOnboarding)) {
      const hasBagClubs = userProfile?.golfBag && userProfile.golfBag.length > 0;
      if (!hasBagClubs) return; // Wait until clubs are populated

      const hasSeenWalkthrough = localStorage.getItem(`hasSeenWalkthrough_${userProfile.id}`);
      if (!hasSeenWalkthrough) {
        setTimeout(() => {
          setIsVisible(true);
        }, 800);
      }
    }
  }, [userProfile, forceShow]);

  // Navigate to the step's route when step changes
  useEffect(() => {
    const targetRoute = TOUR_STEPS[currentStep].route;
    if (isVisible && targetRoute && location.pathname !== targetRoute) {
      navigate(targetRoute);
    }
  }, [currentStep, isVisible, navigate, location.pathname]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 150);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    if (userProfile?.id) {
      localStorage.setItem(`hasSeenWalkthrough_${userProfile.id}`, 'true');
    }
    setIsVisible(false);
    // Navigate to My Golf Bag to encourage setup
    navigate('/my-golf-bag');
    onComplete();
  };

  const goToStep = (stepIndex: number) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(stepIndex);
      setIsAnimating(false);
    }, 150);
  };

  if (!isVisible) return null;

  const step = TOUR_STEPS[currentStep];
  const StepIcon = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop - no blur so user can see the page context */}
      <div 
        className="absolute inset-0 bg-black/40 transition-opacity duration-300" 
        onClick={handleSkip} 
      />

      {/* Tour Card */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all duration-300 ${
          isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
        }`}
      >
        {/* Gradient Header */}
        <div className={`bg-gradient-to-r ${step.accentColor} p-6 text-white`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <StepIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-medium">
                  Step {currentStep + 1} of {TOUR_STEPS.length}
                </p>
                <h3 className="text-xl font-bold">{step.title}</h3>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="Close tour"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5 justify-center">
            {TOUR_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => goToStep(index)}
                className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentStep 
                    ? 'w-8 bg-white' 
                    : index < currentStep 
                      ? 'w-2.5 bg-white/70' 
                      : 'w-2.5 bg-white/30'
                }`}
                aria-label={`Go to step ${index + 1}`}
                style={{ minHeight: '24px', minWidth: '24px', display: 'flex', alignItems: 'center' }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 text-base leading-relaxed mb-4">
            {step.description}
          </p>

          {/* Tip box */}
          {step.tip && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
                <span className="text-lg">💡</span>
                <span>{step.tip}</span>
              </p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <button
                onClick={handlePrevious}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 font-semibold transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className={`flex items-center justify-center gap-2 bg-gradient-to-r ${step.accentColor} text-white px-6 py-3 rounded-xl hover:shadow-lg font-semibold transition-all ${
                isFirstStep ? 'flex-1' : 'flex-[2]'
              }`}
            >
              {isLastStep ? (
                <>
                  Let's Go!
                  <Rocket className="w-4 h-4" />
                </>
              ) : isFirstStep ? (
                <>
                  Start Tour
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleSkip}
            className="mt-4 text-sm text-gray-400 hover:text-gray-600 text-center w-full transition-colors"
          >
            Skip tour — I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
